/**
 * Variant D Phase 1 — typed visibility metadata from un-prefixed
 * raw-groups.
 *
 * Scope: ONLY Visibility parameters and their child states.
 * Flip / Linear / Rotation / Lookup / Stretch / Polar / Array etc. are
 * out of scope and land in later Variant D phases. This module closes
 * the feedback loop for `evaluateBlock` → `filterVisibleEntities` on
 * real AutoCAD 2018+ output (АБИ1.dxf) where the dynamic-block
 * authoring classes land in `rawObjectGroups[]` rather than
 * `dynamicBlockObjects[]` because they emit without the `ACDB` prefix.
 *
 * ── Why a second pass and not a rewrite of the prefixed parser? ──
 *
 * The prefixed-path parser (`dxfBridge.dynamic.parser.ts`) is
 * authoritative for ezdxf-style / round-tripped fixtures. Real AutoCAD
 * output emits UN-prefixed classes (`BLOCKVISIBILITYPARAMETER` /
 * `BLOCKVISIBILITYSTATE`) whose raw groups stream into
 * `rawObjectGroups[]` instead. This module parses the un-prefixed
 * stream into the SAME `DynamicBlockMetadata` shape the evaluator
 * already consumes — no new schema, no new evaluator code.
 *
 * Ordering (see `dxfBridge.dynamic.ts:attachDynamicBlockMetadata`):
 *   1. Prefixed-parser pass runs first. If it produces metadata, that
 *      wins.
 *   2. For every remaining block that still has `dynamicMetadata:
 *      undefined` AND is flagged dynamic, we invoke
 *      `buildVisibilityMetadataFromRawGroups`. Only visibility-bearing
 *      blocks get typed metadata from this module.
 *
 * Records are NOT removed from `rawObjectGroups[]` when we parse them —
 * the writer's round-trip path re-emits them verbatim. This module
 * produces a parallel typed view, not a replacement.
 */

import type {
  DxfObjectsSection,
  DxfTablesSection,
} from "@dxf-ai-editor/dxf-io";

import type { BlockDefinition } from "./model";
import type {
  DynamicBlockMetadata,
  VisibilityParameter,
  VisibilityState,
} from "./dynamicBlock";
import {
  dynamicParameterSchema,
  validateDynamicMetadata,
} from "./dynamicBlock";
import {
  collectGroups,
  decodeInlineStatesFromBvpGroups,
  firstGroup,
  firstInt,
  parsePointAt,
} from "./dxfBridge.dynamic.shared";
import {
  buildBlockToEvalGraph,
  collectVisibilityStates,
  isVisibilityParameter,
  isVisibilityState,
  splitRawRecords,
  type RawRecord,
} from "./dxfBridge.dynamic.params";

// ═══════════════════════════════════════════════════════════════════════════
// Internal types
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fully-decoded state record keyed by handle — the bookkeeping needed
 * to merge inline group-303 names with child `BLOCKVISIBILITYSTATE`
 * records. We collect states eagerly because AutoCAD emits a single
 * flat list in no particular order, and we need to resolve by both
 * handle AND name.
 */
interface DecodedState {
  readonly handle: string;
  readonly name: string;
  readonly invisibleEntityIds: readonly string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// State collection — extends params.ts helper with entity-handle decode
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Walk raw records and return every `BLOCKVISIBILITYSTATE` record
 * fully decoded: name + invisibleEntityIds. The existing
 * `collectVisibilityStates` helper in params.ts only decodes name
 * (sufficient for Properties-palette defaults). Typed metadata needs
 * the entity-handle list too, so we re-walk here.
 *
 * Group codes:
 *   - 2 (AutoCAD) OR 300 (ezdxf) — state name
 *   - 332 (soft pointer, preferred) — invisible entity handles
 *   - 340 (hard pointer, alternate) — invisible entity handles
 *
 * AutoCAD 2018+ emits group 332 for each invisible entity. Older
 * encoders and ezdxf use 340. We accept both and concatenate (handles
 * are verbatim — no dedup needed since AutoCAD never emits the same
 * entity handle twice in one state).
 */
function decodeStatesByOwner(
  records: readonly RawRecord[],
): Map<string, DecodedState[]> {
  const byOwner = new Map<string, DecodedState[]>();
  for (const rec of records) {
    if (!isVisibilityState(rec.type)) continue;
    const owner = rec.ownerHandle;
    if (!owner) continue;
    const name =
      firstGroup(rec.groups, 2)?.trim() ??
      firstGroup(rec.groups, 300)?.trim() ??
      `state_${rec.handle}`;
    const pointer332 = collectGroups(rec.groups, 332)
      .map((h) => h.trim())
      .filter((h) => h.length > 0);
    const pointer340 = collectGroups(rec.groups, 340)
      .map((h) => h.trim())
      .filter((h) => h.length > 0);
    const invisibleEntityIds: readonly string[] = [
      ...pointer332,
      ...pointer340,
    ];
    const state: DecodedState = {
      handle: rec.handle,
      name,
      invisibleEntityIds,
    };
    const list = byOwner.get(owner);
    if (list) list.push(state);
    else byOwner.set(owner, [state]);
  }
  return byOwner;
}

// ═══════════════════════════════════════════════════════════════════════════
// Inline per-state entity decoder — AutoCAD 2018+ BVP inline encoding
// ═══════════════════════════════════════════════════════════════════════════
//
// The inline decoder now lives in `dxfBridge.dynamic.shared.ts` and is
// consumed by BOTH this pass-2 un-prefixed path AND the pass-1 prefixed
// parser (`dxfBridge.dynamic.parser.ts`). See the JSDoc on
// `decodeInlineStatesFromBvpGroups` for the encoding layout.

// ═══════════════════════════════════════════════════════════════════════════
// Parameter decoder — one visibility parameter → VisibilityParameter
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Decode a single `BLOCKVISIBILITYPARAMETER` raw-record into a typed
 * `VisibilityParameter`. Returns `undefined` when the record has no
 * usable state list (encoding is malformed). The caller logs a
 * once-per-block warning and skips the parameter.
 *
 * Merge rules (from plan §5.6):
 *   - Inline 303 list non-empty → take NAMES from inline, in order.
 *     For each name look up child state by NAME (NOT handle) to get
 *     invisibleEntityIds + id. No match → synthesize id
 *     `${parameterHandle}-inline-${index}`, invisibleEntityIds = [].
 *   - Inline 303 list empty → take states directly from child records
 *     keyed by ownerHandle.
 *   - defaultStateId / currentStateId: if group 340/341 matches a
 *     known state (by HANDLE), use it; else first state's id.
 *   - Case sensitivity: "Wide" and "wide" are DISTINCT names.
 *   - Dedupe inline 303 by first-occurrence; preserve authoring order.
 */
function decodeVisibilityParameter(
  rec: RawRecord,
  statesByOwner: ReadonlyMap<string, readonly DecodedState[]>,
): VisibilityParameter | undefined {
  const groups = rec.groups;
  const name =
    firstGroup(groups, 300)?.trim() ??
    firstGroup(groups, 1)?.trim() ??
    `visibility_${rec.handle}`;
  const position = parsePointAt(groups, 10, 20);
  const label = firstGroup(groups, 3)?.trim() ?? "";
  const description = firstGroup(groups, 4)?.trim() ?? "";
  const chainActions = (firstInt(groups, 280) ?? 0) === 1;
  const showGrip = (firstInt(groups, 281) ?? 1) !== 0;

  const childStates = statesByOwner.get(rec.handle) ?? [];
  const childByName = new Map<string, DecodedState>();
  for (const s of childStates) {
    // Authored order matters — first-occurrence wins when AutoCAD
    // emits duplicate state names (rare, but possible as round-trip
    // residue). Preserve authoring order by not overwriting.
    if (!childByName.has(s.name)) childByName.set(s.name, s);
  }

  // Inline group-303 names — AutoCAD 2018+ dominant encoding. Dedupe
  // by first occurrence, preserving order.
  const inlineNames = collectGroups(groups, 303)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const seen = new Set<string>();
  const inlineDeduped: string[] = [];
  for (const n of inlineNames) {
    if (!seen.has(n)) {
      seen.add(n);
      inlineDeduped.push(n);
    }
  }

  // Decode per-state inline invisible-entity handles (AutoCAD 2018+
  // dominant encoding — child BLOCKVISIBILITYSTATE records are absent
  // and entity lists live directly inside the BVP groups).
  const inlineStateEntities = decodeInlineStatesFromBvpGroups(groups);

  // Build the final state list based on inline-vs-child merge rules.
  const states: VisibilityState[] = [];
  if (inlineDeduped.length > 0) {
    // Encoding A or A+B — inline is authoritative. Prefer inline
    // per-state entity list (AutoCAD 2018+); fall back to child
    // BLOCKVISIBILITYSTATE record if inline list is empty AND a
    // matching child exists (ezdxf-style round-trip fixtures).
    for (let i = 0; i < inlineDeduped.length; i += 1) {
      const stateName = inlineDeduped[i]!;
      const inlineEntities = inlineStateEntities.get(stateName);
      const child = childByName.get(stateName);
      if (inlineEntities && inlineEntities.length > 0) {
        // Encoding A (AutoCAD 2018+ inline) — entity handles come
        // from the BVP record itself. Child record, if any, is
        // redundant; we keep its handle as id for stability with
        // downstream references.
        states.push({
          id: child ? child.handle : `${rec.handle}-inline-${i}`,
          name: stateName,
          invisibleEntityIds: [...inlineEntities],
        });
      } else if (child) {
        // Encoding A+B (ezdxf-style with child records) — no inline
        // entities, use the child record's list.
        states.push({
          id: child.handle,
          name: stateName,
          invisibleEntityIds: [...child.invisibleEntityIds],
        });
      } else {
        // Encoding C (inline-only, no entity list) — no child state
        // with matching name and no inline entities. Synthesize id
        // so the schema requirement (id.min(1)) is met;
        // invisibleEntityIds stays empty, meaning AutoCAD-legal
        // "dropdown works but no geometry toggle".
        states.push({
          id: `${rec.handle}-inline-${i}`,
          name: stateName,
          invisibleEntityIds: [],
        });
      }
    }
  } else {
    // Encoding B (ezdxf-synthetic / older AutoCAD) — states come
    // from child records keyed by ownerHandle.
    for (const child of childStates) {
      states.push({
        id: child.handle,
        name: child.name,
        invisibleEntityIds: [...child.invisibleEntityIds],
      });
    }
  }

  if (states.length === 0) {
    // Malformed parameter — no states found via inline or children.
    // Caller logs and skips. Zod would reject this anyway
    // (`states.min(1)`) — doing it explicitly keeps the error path
    // local rather than deep inside Zod.
    return undefined;
  }

  // Resolve default + current state IDs. Group 340 = default state
  // handle; group 341 = current state handle. Both are handle
  // references into our decoded state list. If present but don't
  // match any state, fall back to the first state's id.
  const defaultHandleRef = firstGroup(groups, 340)?.trim() ?? "";
  const currentHandleRef = firstGroup(groups, 341)?.trim() ?? "";
  const firstStateId = states[0]!.id;
  const pickByHandle = (handleRef: string): string | undefined => {
    if (!handleRef) return undefined;
    return states.find((s) => s.id === handleRef)?.id;
  };
  const defaultStateId = pickByHandle(defaultHandleRef) ?? firstStateId;
  const currentStateId = pickByHandle(currentHandleRef) ?? defaultStateId;

  const parameter: VisibilityParameter = {
    kind: "visibility",
    id: rec.handle,
    name,
    label,
    description,
    chainActions,
    showGrip,
    position,
    states,
    defaultStateId,
    currentStateId,
  };

  return parameter;
}

// ═══════════════════════════════════════════════════════════════════════════
// Empty-metadata synth — one visibility-bearing block's typed view
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a fresh `DynamicBlockMetadata` carrying only the supplied
 * visibility parameters. Other metadata collections (actions, lookup
 * tables, constraints, eval-graph, blockPropertiesTable) stay empty —
 * the evaluator's visibility filter is the only consumer for Phase 1.
 * Subsequent Variant D phases will union these metadata into a single
 * container.
 */
function synthesizeVisibilityMetadata(
  parameters: readonly VisibilityParameter[],
): DynamicBlockMetadata {
  return {
    version: 1,
    parameters: [...parameters],
    actions: [],
    evaluationGraph: { edges: [], chainGroups: [] },
    lookupTables: [],
    geometricConstraints: [],
    grips: [],
    futureExtensions: {},
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API — block-name → DynamicBlockMetadata
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Scan the OBJECTS section for un-prefixed `BLOCKVISIBILITYPARAMETER`
 * records and return a map from block name to typed
 * `DynamicBlockMetadata` carrying only those parameters (plus any
 * child states).
 *
 * Resolution chain per block:
 *   1. `buildBlockToEvalGraph` maps block name → ACAD_EVALUATION_GRAPH
 *      handle (either via ACAD_ENHANCEDBLOCK or AcDbBlockRepresentation
 *      dictionary entry, per plan Edge #13).
 *   2. Records whose ownerHandle == eval-graph handle are the
 *      block's parameters/states.
 *   3. Only `BLOCKVISIBILITYPARAMETER` records are decoded; other
 *      parameter classes are ignored (Phase 1 scope).
 *
 * A parameter that decodes to zero states is logged (once-per-block
 * warning) and SKIPPED — the resulting metadata still validates.
 * Blocks with no valid parameters don't appear in the output map, so
 * the caller can skip their metadata attachment.
 *
 * @param tablesSection   TABLES section for BLOCK_RECORD / dict lookup
 * @param objectsSection  OBJECTS section carrying rawObjectGroups
 * @param blocks          block-name → BlockDefinition (from importer)
 * @returns               block-name → synthesized DynamicBlockMetadata
 */
export function buildVisibilityMetadataFromRawGroups(
  tablesSection: DxfTablesSection | null,
  objectsSection: DxfObjectsSection | null,
  blocks: Readonly<Record<string, BlockDefinition>>,
): Map<string, DynamicBlockMetadata> {
  const out = new Map<string, DynamicBlockMetadata>();
  if (!objectsSection) return out;

  // DYN-4 Phase 3 regression fix — Phase 1 (commit 1b7f330) expanded
  // `isSupportedDynamicBlockType` so un-prefixed BLOCK* classes now land
  // in `dynamicBlockObjects[]` instead of `rawObjectGroups[]`. Real AutoCAD
  // 2018+ output (АБИ1.dxf) keeps its visibility records under
  // `dynamicBlockObjects`; pre-Phase-1 fixtures still stream into
  // `rawObjectGroups`. Scan BOTH so pass-2 finds visibility params in
  // every shape.
  const rawRecords = splitRawRecords(objectsSection.rawObjectGroups);
  const dynObjRecords: RawRecord[] = objectsSection.dynamicBlockObjects.map(
    (obj) => ({
      type: obj.type,
      handle: obj.handle,
      ownerHandle: obj.ownerHandle ?? "",
      groups: obj.rawGroups,
    }),
  );
  const records: RawRecord[] = [...rawRecords, ...dynObjRecords];
  if (records.length === 0) return out;
  // `collectVisibilityStates` (params.ts) only decodes name — we need
  // invisibleEntityIds too, so use our local richer collector.
  const statesByOwner = decodeStatesByOwner(records);
  // Eagerly ensure the shared helper is considered reachable — it is
  // the canonical name-only view consumed by stage-A in params.ts and
  // kept around for parity. Using a void expression keeps the import
  // tree explicit (splitting the two collectors underlines that
  // params.ts owns name-decoding and this file owns typed-metadata
  // decoding).
  void collectVisibilityStates;

  const blockToGraph = buildBlockToEvalGraph(tablesSection, objectsSection);
  if (blockToGraph.size === 0) return out;

  // Group records by ownerHandle for O(1) per-block lookup.
  const recordsByOwner = new Map<string, RawRecord[]>();
  for (const rec of records) {
    if (!rec.ownerHandle) continue;
    const list = recordsByOwner.get(rec.ownerHandle);
    if (list) list.push(rec);
    else recordsByOwner.set(rec.ownerHandle, [rec]);
  }

  for (const [blockName] of Object.entries(blocks)) {
    const evalHandle = blockToGraph.get(blockName);
    if (!evalHandle) continue;
    // A block has an eval-graph handle iff its BLOCK_RECORD carries an
    // extension DICTIONARY with an ACAD_ENHANCEDBLOCK /
    // AcDbBlockRepresentation entry — this is AutoCAD's canonical
    // "dynamic block" marker (per dxfBridge.dynamic.detect.ts:1-50).
    // We deliberately do NOT gate on `block.dynamicBlock` because the
    // attach-metadata bridge runs BEFORE `detectDynamicBlocks`, so
    // that flag is unpopulated here. The eval-graph presence check is
    // the earlier, independent signal for the same fact.
    const evalChildren = recordsByOwner.get(evalHandle) ?? [];
    const parameters: VisibilityParameter[] = [];
    let skipped = 0;
    for (const rec of evalChildren) {
      if (!isVisibilityParameter(rec.type)) continue;
      try {
        const param = decodeVisibilityParameter(rec, statesByOwner);
        if (!param) {
          skipped += 1;
          continue;
        }
        // Zod round-trip — every typed parameter MUST pass its
        // schema. If it doesn't, treat as malformed (same as zero
        // states) — log and skip rather than emit invalid metadata.
        const parsed = dynamicParameterSchema.safeParse(param);
        if (!parsed.success) {
          skipped += 1;
          continue;
        }
        // safeParse.data has the Zod defaults applied; use it so the
        // out-map always carries schema-normalized values.
        parameters.push(parsed.data as VisibilityParameter);
      } catch {
        skipped += 1;
      }
    }
    if (skipped > 0 && typeof console !== "undefined") {
      console.warn(
        `[dxfBridge.dynamic.visibility] ${blockName}: skipped ${skipped} malformed BLOCKVISIBILITYPARAMETER record(s)`,
      );
    }
    if (parameters.length === 0) continue;
    const meta = synthesizeVisibilityMetadata(parameters);
    // Final safety net — if the metadata somehow fails semantic
    // validation (e.g. duplicate state ids across inline+child
    // merge), don't attach it. In practice this never fires for
    // real АБИ1 input, but the guard keeps contract strict per
    // priority #1 (no invalid metadata leaks).
    const errors = validateDynamicMetadata(meta);
    if (errors.length > 0) {
      if (typeof console !== "undefined") {
        console.warn(
          `[dxfBridge.dynamic.visibility] ${blockName}: metadata invalid after synthesis — ${errors.join("; ")}`,
        );
      }
      continue;
    }
    out.set(blockName, meta);
  }

  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Test hooks
// ═══════════════════════════════════════════════════════════════════════════

/** Internal helpers re-exported for unit tests only. */
export const __testables = {
  decodeStatesByOwner,
  decodeVisibilityParameter,
  decodeInlineStatesFromBvpGroups,
  synthesizeVisibilityMetadata,
};
